import { HttpParams } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { EbwModalService } from '@ebw-monorepo/modal';
import { UtilsService } from '@ebw-monorepo/utils';
import moment from 'moment';
import { filter, map } from 'rxjs';
import { CustomvalidationService } from './../../services/customvalidation.service';
import { ConfigSidebarService } from '@ebw-monorepo/map';
@Component({
  selector: 'ebw-monorepo-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss']
})
export class ChangePasswordComponent implements OnInit {
  public formPwType = 'password';
  public formOldPasswordType = 'password';
  //Constants
  public PASSWORD_TYPE = 'password';
  public TEXT_TYPE = 'text';
  // se true non permette di chiudere la modale
  public undisposableDialog = false;

  public form: FormGroup;

  public expiredDateStr?: string;
  public isPwdExpired?: boolean;

  // ---------------------------------------------------------------------------------------------------------

  constructor(
    public dialogRef: MatDialogRef<ChangePasswordComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { url: string; user_id: string; expiredDate: string; to_confirm?: number },
    private utilsSrv: UtilsService,
    private translateService: I18NService,
    private ebwModal: EbwModalService,
    private validatorSrv: CustomvalidationService,
    private sidebarConfigService: ConfigSidebarService
  ) {
    this.form = new FormBuilder().group({
      oldPassword: ['', [Validators.required]],
      password: ['', [Validators.required, this.patternValidator]],
      confirmPassword: ['', [Validators.required, this.checkPasswords]]
    });

    if (this.data.expiredDate) {
      const expiredDate = moment(this.data.expiredDate);
      this.expiredDateStr = expiredDate.format('DD/MM/YYYY');
      this.isPwdExpired = expiredDate.isBefore();
    }

    if (this.data.to_confirm || this.isPwdExpired) {
      this.undisposableDialog = true;
    }
  }

  // ---------------------------------------------------------------------------------------------------------

  async ngOnInit(): Promise<void> {
    // mi metto in ascolto sui cambiamenti del form
    this.form.valueChanges
      .pipe(
        // controllo solo eventi in cui esistono entrambe le nuove password
        filter(({ password, confirmPassword }) => password && confirmPassword),
        // ritorno true/false se le password sono uguali
        // client-side UI match check between two fields the same user just typed; not an auth boundary
        // nosemgrep: nodejs_scan.javascript-crypto-rule-node_timing_attack, eslint.detect-possible-timing-attacks
        map(({ password, confirmPassword }) => password === confirmPassword)
      )
      .subscribe({
        // se le password sono uguali resetto gli errori sul campo input confirmPassword
        // fatto per far cambiare stato all'input confirmPassword quando cambia il valore del campo password
        next: (samePassword: boolean) => {
          if (samePassword) {
            this.form.controls.confirmPassword.setErrors(null);
            return;
          }
          this.form.controls.confirmPassword.setErrors({ notSame: true });
        },
        error: (error) => console.error('[ngOnInit][form.valueChanges]', error)
      });
  }

  // ---------------------------------------------------------------------------------------------------------

  changePassword() {
    const passwordField = this.form.get('password');
    const password = passwordField?.value;
    const oldPassword = this.form.get('oldPassword')?.value;
    const samePassword = password === oldPassword && password;

    if (samePassword) {
      const errors = passwordField?.errors;
      passwordField?.setErrors({ ...errors, sameOldPasswordError: true });
    }

    // se premo sul bottone di completamento del processo nel form,
    // marco come touched i campi input se questi ultimi non sono stati ancora selezionati.
    // Facendo ciò vengono mostratigli errori a video
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    let params = new HttpParams().set('id', this.data.user_id).set('old_password', oldPassword).set('password', password);

    const title = this.translateService.getTranslateParameter('login.update_password');
    this.utilsSrv.postRequest(this.data.url, params, false).subscribe({
      next: () => {
        const content = this.translateService.getTranslateParameter('login.update_password_success');
        this.ebwModal
          .openSuccessModal({ content, title })
          .afterClosed()
          .subscribe({
            next: (r) => {
              this.dialogRef.close('CONFIRM');
            },
            error: (e) => console.log(e)
          });
      },
      error: (err) => {
        const content =
          this.translateService.getTranslateParameter('login.update_password_failure') + `\n${err.error.message ? err.error.message : ''}`;
        this.ebwModal.openErrorModal({ title, content });
      }
    });
  }

  // ---------------------------------------------------------------------------------------------------------

  /**
   * Chiude il dialog forzato senza aver cambiato la password: il chiamante deve sloggare l'utente,
   * che al prossimo login si ritroverà di nuovo questo stesso form finché non completa il cambio.
   */
  cancelForced() {
    this.dialogRef.close('CANCEL');
  }

  // ---------------------------------------------------------------------------------------------------------

  /**
   * Change the visibility of the password text by toggling the formPwType between "text" and "password"
   */
  togglePasswordVisibility(field?: string) {
    switch (field) {
      case 'oldPassword':
        this.formOldPasswordType = this.formOldPasswordType == this.PASSWORD_TYPE ? this.TEXT_TYPE : this.PASSWORD_TYPE;
        break;
      default:
        this.formPwType = this.formPwType == this.PASSWORD_TYPE ? this.TEXT_TYPE : this.PASSWORD_TYPE;
    }
  }

  // ---------------------------------------------------------------------------------------------------------

  checkPasswords: ValidatorFn = (control: any): ValidationErrors | null => {
    if (!this.form) {
      return null;
    }
    const password = this.form.controls.password.value;
    const confirmPassword = control.value;
    const isSamePassword = password === confirmPassword;
    return isSamePassword ? null : { notSame: true };
  };

  // ---------------------------------------------------------------------------------------------------------

  patternValidator: ValidatorFn = (control: any): ValidationErrors | null => this.validatorSrv.patternValidator()(control);
}
